import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Video, Star, Clock, DollarSign, ArrowLeft, X } from 'lucide-react';
import { toast } from 'sonner';
import { grossFromNet, fmtMoney } from '../lib/fees';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import './Bookings.css';

const Bookings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');
  const [callState, setCallState] = useState(null); // { booking, roomUrl, token }
  const [showRating, setShowRating] = useState(null);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null); // booking to cancel

  useEffect(() => {
    if (!user) return;
    const fetchBookings = async () => {
      try {
        const data = await api(`/api/bookings/user/${user.id}?booking_type=${tab}`);
        setBookings(data.bookings || []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetchBookings();
  }, [user, tab]);

  // Check if returning from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      api(`/api/payments/status/${sessionId}`).then(() => {
        window.history.replaceState({}, '', '/bookings');
        // Refresh bookings
        if (user) api(`/api/bookings/user/${user.id}?booking_type=upcoming`).then(d => setBookings(d.bookings || []));
      }).catch(() => {});
    }
  }, [user]);

  const startCall = (booking) => {
    // Send both parties to the lobby page instead of opening iframe inline.
    // The lobby handles the 2-minute countdown, presence gating, and no-show
    // resolution before showing the Daily.co room.
    navigate(`/lobby/${booking.id}`);
  };

  const cancelBooking = (b) => setCancelTarget(b);

  const confirmCancel = async () => {
    const b = cancelTarget;
    if (!b) return;
    setCancelTarget(null);
    try {
      const r = await api(`/api/bookings/${b.id}/cancel`, { method: 'POST' });
      if (r.refund_amount > 0) {
        toast.success('Booking cancelled', { description: `Refund of $${r.refund_amount.toFixed(2)} processed.` });
      } else {
        // Distinguish refund-pending (Stripe failed) from 24h-no-refund
        let hoursUntil = null;
        if (b.scheduled_date) hoursUntil = (new Date(b.scheduled_date) - new Date()) / 3.6e6;
        const refundable = hoursUntil === null || hoursUntil >= 24;
        if (refundable) {
          toast.error('Booking cancelled — refund pending', {
            description: 'We couldn’t auto-refund. Our team will process it shortly.',
          });
        } else {
          toast('Booking cancelled', { description: 'No refund issued (inside 24-hour window).' });
        }
      }
      const data = await api(`/api/bookings/user/${user.id}?booking_type=${tab}`);
      setBookings(data.bookings || []);
    } catch (err) {
      toast.error('Cancel failed', { description: err.message });
    }
  };

  const endCall = async () => {
    if (!callState) return;
    try {
      await api(`/api/calls/${callState.booking.id}/end`, { method: 'POST' });
      setShowRating(callState.booking);
    } catch { /* ignore */ }
    setCallState(null);
  };

  const submitRating = async () => {
    if (!showRating) return;
    const creatorId = showRating.creator_id === user.id ? showRating.user_id : showRating.creator_id;
    try {
      await api(`/api/calls/${showRating.id}/rate`, {
        method: 'POST',
        body: JSON.stringify({
          booking_id: showRating.id,
          rater_id: user.id,
          creator_id: creatorId,
          teachability_rating: rating,
          skill_ratings: {},
          review_text: reviewText
        })
      });
      setShowRating(null);
      setRating(5);
      setReviewText('');
      setTab('past');
    } catch (err) {
      alert('Rating failed: ' + err.message);
    }
  };

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return ''; }
  };

  // Video call view
  if (callState) {
    return (
      <div className="bookings-container" data-testid="call-view" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16, background: '#111' }}>
          <h2 style={{ color: '#fff', fontSize: 16, margin: 0 }}>In Call</h2>
          <button onClick={endCall} style={{ background: '#ff4444', color: '#fff', border: 'none', borderRadius: 20, padding: '8px 20px', fontWeight: 600, cursor: 'pointer' }} data-testid="end-call-btn">
            End Call
          </button>
        </div>
        <iframe
          src={`${callState.roomUrl}?t=${callState.token}`}
          style={{ flex: 1, width: '100%', border: 'none' }}
          allow="camera; microphone; fullscreen; speaker; display-capture"
          title="Video Call"
          data-testid="call-iframe"
        />
      </div>
    );
  }

  // Rating dialog
  if (showRating) {
    return (
      <div className="bookings-container" data-testid="rating-view" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#1a1a1a', borderRadius: 16, padding: 24, maxWidth: 400, width: '100%', textAlign: 'center' }}>
          <h2 style={{ color: '#fff', marginBottom: 8 }}>Rate Your Session</h2>
          <p style={{ color: '#888', marginBottom: 20 }}>How was your experience?</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
            {[1, 2, 3, 4, 5].map(s => (
              <button key={s} onClick={() => setRating(s)} style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid={`star-${s}`}>
                <Star size={32} fill={s <= rating ? '#D97706' : 'none'} color={s <= rating ? '#D97706' : '#444'} />
              </button>
            ))}
          </div>
          <textarea
            value={reviewText}
            onChange={e => setReviewText(e.target.value)}
            placeholder="Write a review (optional)..."
            rows={3}
            style={{ width: '100%', background: '#222', border: '1px solid #333', borderRadius: 12, padding: 12, color: '#fff', fontSize: 14, resize: 'none', marginBottom: 16 }}
            data-testid="review-input"
          />
          <button onClick={submitRating} style={{ width: '100%', background: '#E07A5F', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 24px', fontWeight: 600, cursor: 'pointer' }} data-testid="submit-rating-btn">
            Submit Rating
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bookings-container" data-testid="bookings-page">
      <div className="bookings-header">
        <h1>Bookings</h1>
      </div>
      <div className="bookings-tabs" role="tablist">
        <button className={`bookings-tab ${tab === 'upcoming' ? 'active' : ''}`} onClick={() => setTab('upcoming')} data-testid="tab-upcoming" role="tab" aria-selected={tab === 'upcoming'}>Upcoming</button>
        <button className={`bookings-tab ${tab === 'past' ? 'active' : ''}`} onClick={() => setTab('past')} data-testid="tab-past" role="tab" aria-selected={tab === 'past'}>Past</button>
      </div>
      <div className="bookings-list">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading...</div>
        ) : bookings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#666' }}>
            <p>{tab === 'upcoming' ? 'No upcoming bookings' : 'No past bookings'}</p>
          </div>
        ) : (
          bookings.map(b => (
            <div key={b.id} className="booking-card" data-testid={`booking-${b.id}`}>
              <div className="booking-info">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Clock size={14} color="#888" />
                  <span style={{ color: '#888', fontSize: 13 }}>{b.minutes} min session</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <DollarSign size={14} color="#888" />
                  <span style={{ color: '#888', fontSize: 13 }} data-testid={`booking-price-${b.id}`}>{b.creator_id === user.id ? fmtMoney(b.price) : fmtMoney(b.gross_price || grossFromNet(b.price))}</span>
                </div>
                <span className="booking-status" data-testid={`booking-status-${b.id}`} style={{
                  color: b.status === 'confirmed' || b.status === 'room_created' ? '#4ade80' : b.status === 'completed' || b.status === 'expired' ? '#888' : '#ff4444'
                }}>
                  {b.status === 'room_created' ? 'Ready to call' : b.status === 'expired' ? 'Expired — time not agreed' : b.status}
                </span>
                <span style={{ color: '#555', fontSize: 12, marginLeft: 8 }}>{formatDate(b.created_at)}</span>
              </div>
              {(b.status === 'confirmed' || b.status === 'room_created') && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => startCall(b)} className="join-call-btn" data-testid={`join-call-${b.id}`}>
                    <Video size={16} /> Join Call
                  </button>
                  <button onClick={() => cancelBooking(b)} className="cancel-booking-btn" data-testid={`cancel-booking-${b.id}`}>
                    <X size={14} /> Cancel
                  </button>
                </div>
              )}
              {b.status === 'pending' && (
                <button onClick={() => cancelBooking(b)} className="cancel-booking-btn" data-testid={`cancel-booking-${b.id}`}>
                  <X size={14} /> Cancel request
                </button>
              )}
            </div>
          ))
        )}
      </div>

      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent data-testid="cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const b = cancelTarget;
                if (!b?.scheduled_date) return 'You will receive a full refund to your card.';
                const hoursUntil = (new Date(b.scheduled_date) - new Date()) / 3.6e6;
                if (hoursUntil >= 24) {
                  return `Your session is in ${hoursUntil.toFixed(1)} hours. You’ll receive a full refund to your original payment method.`;
                }
                return `Heads up — your session is in ${Math.max(0, hoursUntil).toFixed(1)} hours. Cancellations inside 24 hours are non-refundable.`;
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-dialog-dismiss">Keep booking</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} data-testid="cancel-dialog-confirm">
              Cancel booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Bookings;
